import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTalentAuth } from '../../contexts/TalentAuthContext';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import { Eye, EyeOff } from 'lucide-react';
import './UniversalLogin.css';

interface UniversalLoginProps {
  userType?: 'talent' | 'customer' | 'client';
}

declare global {
  interface Window {
    google: any;
  }
}

const UniversalLogin: React.FC<UniversalLoginProps> = ({ userType }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const googleButtonRef = React.useRef<HTMLDivElement>(null);

  const { login: talentLogin, loginWithGoogle: talentLoginWithGoogle } = useTalentAuth();
  const { login: customerLogin, loginWithGoogle: customerLoginWithGoogle } = useCustomerAuth();
  const navigate = useNavigate();

  // Handle Google OAuth callback
  const handleGoogleCallback = useCallback(async (response: any) => {
    try {
      setIsGoogleLoading(true);
      setMessage({ type: '', text: '' });
      
      if (userType === 'customer' || userType === 'client') {
        // Try customer Google login
        const customerResult = await customerLoginWithGoogle(response.credential);
        if (customerResult.success) {
          setMessage({ type: 'success', text: 'Google login successful! Redirecting...' });
          navigate('/customer/dashboard');
          return;
        }
      } else if (userType === 'talent') {
        // Try talent login
        const talentResult = await talentLoginWithGoogle(response.credential);
        if (talentResult.success) {
          setMessage({ type: 'success', text: 'Google login successful! Redirecting...' });
          navigate('/talent/dashboard');
          return;
        }
      } else {
        // Universal login - try talent first, then customer
        try {
          const talentResult = await talentLoginWithGoogle(response.credential);
          if (talentResult.success) {
            setMessage({ type: 'success', text: 'Google login successful! Redirecting...' });
            navigate('/talent/dashboard');
            return;
          }
        } catch (error) {
          // If talent login fails, try customer
          try {
            const customerResult = await customerLoginWithGoogle(response.credential);
            if (customerResult.success) {
              setMessage({ type: 'success', text: 'Google login successful! Redirecting...' });
              navigate('/customer/dashboard');
              return;
            }
          } catch (customerError) {
            // Both failed
          }
        }
      }
      
      setMessage({ type: 'error', text: 'Google login failed. Account not found or login error.' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Google login failed. Please try again.' });
    } finally {
      setIsGoogleLoading(false);
    }
  }, [talentLoginWithGoogle, customerLoginWithGoogle, navigate, userType]);

  // Initialize Google OAuth
  useEffect(() => {
    let isInitialized = false;

    const initializeGoogleOAuth = () => {
      if (isInitialized) return;
      if (window.google?.accounts?.id) {
        try {
          window.google.accounts.id.initialize({
            client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID || '877295110750-se0qnm049lgsn63pga699j5npljnc6oq.apps.googleusercontent.com',
            callback: handleGoogleCallback,
            auto_select: false,
            cancel_on_tap_outside: true,
          });
          isInitialized = true;

          if (googleButtonRef.current) {
            window.google.accounts.id.renderButton(googleButtonRef.current, {
              type: 'standard',
              theme: 'outline',
              size: 'large',
              text: 'continue_with',
              shape: 'rectangular',
              logo_alignment: 'left',
              width: googleButtonRef.current.offsetWidth || 350,
            });
          }
        } catch (error) {
          console.error("Google OAuth initialization error:", error);
        }
      }
    };

    // Load Google OAuth script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = initializeGoogleOAuth;
    document.head.appendChild(script);

    // If script is already loaded
    if (window.google?.accounts?.id) {
      initializeGoogleOAuth();
    }

    return () => {
      document.head.removeChild(script);
    };
  }, [handleGoogleCallback]);

  // Handle Google sign-in button click (fallback/legacy)
  const handleGoogleSignIn = () => {
    if (window.google && window.google.accounts) {
      window.google.accounts.id.prompt();
    } else {
      setMessage({ type: 'error', text: 'Google OAuth is not loaded. Please refresh the page.' });
    }
  };

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear errors when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password.trim()) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters long';
    }

    return newErrors;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      if (userType === 'customer' || userType === 'client') {
        // Customer login only
        const result = await customerLogin(formData.email, formData.password);
        if (result.success) {
          setMessage({ type: 'success', text: 'Login successful! Redirecting...' });
          setTimeout(() => navigate('/customer/dashboard'), 1000);
        } else {
          setMessage({ type: 'error', text: result.message || 'Login failed. Please check your credentials.' });
        }
      } else if (userType === 'talent') {
        // Talent login only
        const result = await talentLogin(formData.email, formData.password);
        if (result.success) {
          setMessage({ type: 'success', text: 'Login successful! Redirecting...' });
          setTimeout(() => navigate('/talent/dashboard'), 1000);
        } else {
          setMessage({ type: 'error', text: result.message || 'Login failed. Please check your credentials.' });
        }
      } else {
        // Universal login - try both
        try {
          // Try talent login first
          const talentResult = await talentLogin(formData.email, formData.password);
          if (talentResult.success) {
            setMessage({ type: 'success', text: 'Login successful! Redirecting...' });
            setTimeout(() => navigate('/talent/dashboard'), 1000);
            return;
          }
        } catch (error) {
          // If talent login fails, try customer login
          try {
            const customerResult = await customerLogin(formData.email, formData.password);
            if (customerResult.success) {
              setMessage({ type: 'success', text: 'Login successful! Redirecting...' });
              setTimeout(() => navigate('/customer/dashboard'), 1000);
              return;
            }
          } catch (error) {
            // Both failed
          }
        }

        setMessage({ type: 'error', text: 'Login failed. Please check your credentials.' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'An unexpected error occurred. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTitle = () => {
    if (userType === 'customer' || userType === 'client') return 'Customer Login';
    if (userType === 'talent') return 'Talent Login';
    return 'Sign In to Your Account';
  };

  const getRegisterLink = () => {
    if (userType === 'customer' || userType === 'client') return '/register?type=customer';
    if (userType === 'talent') return '/register?type=talent';
    return '/register';
  };

  return (
    <div className="talent-login">
      <div className="container-fluid">
        <div className="row min-vh-100">
          {/* Left side - Login Form */}
          <div className="col-lg-6 d-flex align-items-center justify-content-center">
            <div className="login-form-container">
              <div className="text-center mb-4">
                <h2 className="login-title">{getTitle()}</h2>
                <p className="login-subtitle">
                  {userType ? `Welcome back! Please sign in to your ${userType} account.` : 'Welcome back! Please sign in to your account.'}
                </p>
              </div>

              {/* Display message */}
              {message.text && (
                <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-danger'} mb-3`}>
                  {message.text}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label htmlFor="email" className="form-label">
                    Email Address
                  </label>
                  <input
                    type="email"
                    className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter your email"
                    disabled={isSubmitting}
                  />
                  {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                </div>

                <div className="mb-3">
                  <label htmlFor="password" className="form-label">
                    Password
                  </label>
                  <div className="password-input-wrapper">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="Enter your password"
                      disabled={isSubmitting}
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={isSubmitting}
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  {errors.password && <div className="invalid-feedback">{errors.password}</div>}
                </div>

                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="form-check">
                    <input className="form-check-input" type="checkbox" id="rememberMe" />
                    <label className="form-check-label" htmlFor="rememberMe">
                      Remember me
                    </label>
                  </div>
                  <span 
                    className="link-placeholder" 
                    onClick={() => navigate(userType === 'talent' ? '/talent/forgot-password' : '/customer/forgot-password')}
                  >
                    Forgot password?
                  </span>
                </div>

                <button
                  type="submit"
                  className="btn btn-primary btn-login w-100 mb-3"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Signing in...' : 'Sign In'}
                </button>

                <div className="divider mb-3">
                  <span>or</span>
                </div>

                <div 
                  ref={googleButtonRef} 
                  className="w-100 mb-3 d-flex justify-content-center"
                  style={{ minHeight: '44px' }}
                ></div>
              </form>

              <div className="text-center">
                <p className="signup-text">
                  Don't have an account?{' '}
                  <Link to={getRegisterLink()} className="signup-link">
                    Sign up here
                  </Link>
                </p>
              </div>
            </div>
          </div>

          {/* Right side - Image/Branding */}
          <div className="col-lg-6 d-none d-lg-flex align-items-center justify-content-center login-bg">
            <div className="login-image-content">
              <h3 className="text-white mb-3">
                {userType === 'talent' ? 'Showcase Your Skills' :
                  userType === 'customer' || userType === 'client' ? 'Find the Perfect Talent' :
                    'Connect, Collaborate, Create'}
              </h3>
              <p className="text-white-50">
                {userType === 'talent' ? 'Join our platform to connect with clients and grow your business.' :
                  userType === 'customer' || userType === 'client' ? 'Discover talented professionals for your projects.' :
                    'The ultimate platform for talent and opportunity.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UniversalLogin;